/**
 * Helper functions for extracting question numbers and processing questions
 */

/**
 * Expand question range string to array of question numbers
 * @param {string} range - Range string like "17-18" or single number "5"
 * @returns {string[]} Array of question numbers
 */
function expandQuestionRange(range) {
  if (!range) return [];
  if (/^\d+$/.test(range.trim())) {
    return [range.trim()];
  }
  const rangeMatch = range.trim().match(/^(\d+)-(\d+)$/);
  if (rangeMatch) {
    const start = parseInt(rangeMatch[1]);
    const end = parseInt(rangeMatch[2]);
    if (start <= end) {
      const numbers = [];
      for (let i = start; i <= end; i++) {
        numbers.push(i.toString());
      }
      return numbers;
    }
  }
  return [];
}

/**
 * Extract question number from a node (table element or regular element)
 */
export function extractQuestionNumberFromNode(span) {
  let node = span;
  let qNum = '';
  
  // First, try to find .question-box with data-question-number
  while (node && node.tagName !== 'BODY') {
    let questionBox = node.querySelector?.('.question-box');
    if (!questionBox && node.classList?.contains('question-box')) {
      questionBox = node;
    }
    if (questionBox) {
      qNum = questionBox.getAttribute('data-question-number') || questionBox.getAttribute('questionnumber');
      if (qNum) break;
    }
    node = node.parentNode;
  }
  
  // Fallback: try to extract from <1> pattern in the parent <p>
  if (!qNum) {
    const parentP = span.closest('p');
    if (parentP) {
      const numberMatch = parentP.innerHTML.match(/(&lt;(\d+)&gt;|<(\d+)>)/);
      if (numberMatch) {
        qNum = numberMatch[2] || numberMatch[3];
      }
    }
  }
  
  return qNum;
}

/**
 * Get section question numbers from passage text
 */
export function getSectionQuestionNumbers(section) {
  if (!section || !section.passage_text) return [];

  const seen = new Set();
  const questionNumbers = [];
  const parser = new DOMParser();
  const doc = parser.parseFromString(section.passage_text, 'text/html');

  // Extract question numbers from blanks - map each blank to its preceding marker
  doc.querySelectorAll('p').forEach(p => {
    if (p.closest('.question-box')) {
      // Handle question-box paragraphs separately
      const boxContent = p.closest('.question-box')?.querySelector('.question-box-content');
      if (boxContent) {
        boxContent.querySelectorAll('p').forEach(boxP => {
          const pHTML = boxP.innerHTML;
          const markers = [];
          const blanks = [];
          
          const markerRegex = /(&lt;(\d+)&gt;|<(\d+)>)/g;
          let markerMatch;
          while ((markerMatch = markerRegex.exec(pHTML)) !== null) {
            markers.push({
              number: markerMatch[2] || markerMatch[3],
              index: markerMatch.index
            });
          }
          
          boxP.querySelectorAll('.tiptap-input-field').forEach(span => {
            if (!span.closest('table')) {
              const spanHTML = span.outerHTML;
              const spanIndex = pHTML.indexOf(spanHTML);
              if (spanIndex !== -1) {
                blanks.push({ span, index: spanIndex });
              }
            }
          });
          
          blanks.forEach(blank => {
            let matchedMarker = null;
            for (let i = markers.length - 1; i >= 0; i--) {
              if (markers[i].index < blank.index) {
                matchedMarker = markers[i];
                break;
              }
            }
            
            // If matched marker found, use it
            if (matchedMarker && !seen.has(matchedMarker.number)) {
              seen.add(matchedMarker.number);
              questionNumbers.push(matchedMarker.number);
            } 
            // Fallback: Check parent question-box for question number
            else if (!matchedMarker) {
              const questionBox = blank.span.closest('.question-box');
              if (questionBox) {
                const boxQNum = questionBox.getAttribute('data-question-number') || questionBox.getAttribute('questionnumber');
                if (boxQNum && /^\d+$/.test(boxQNum) && !seen.has(boxQNum)) {
                  seen.add(boxQNum);
                  questionNumbers.push(boxQNum);
                }
              }
            }
          });
        });
      }
      return;
    }
    
    // Regular paragraphs
    const pHTML = p.innerHTML;
    const markers = [];
    const blanks = [];
    
    const markerRegex = /(&lt;(\d+)&gt;|<(\d+)>)/g;
    let markerMatch;
    while ((markerMatch = markerRegex.exec(pHTML)) !== null) {
      markers.push({
        number: markerMatch[2] || markerMatch[3],
        index: markerMatch.index
      });
    }
    
    p.querySelectorAll('.tiptap-input-field').forEach(span => {
      if (!span.closest('table')) {
        const spanHTML = span.outerHTML;
        const spanIndex = pHTML.indexOf(spanHTML);
        if (spanIndex !== -1) {
          blanks.push({ span, index: spanIndex });
        }
      }
    });
    
    blanks.forEach(blank => {
      let matchedMarker = null;
      for (let i = markers.length - 1; i >= 0; i--) {
        if (markers[i].index < blank.index) {
          matchedMarker = markers[i];
          break;
        }
      }
      if (matchedMarker && !seen.has(matchedMarker.number)) {
        seen.add(matchedMarker.number);
        questionNumbers.push(matchedMarker.number);
      }
    });
  });

  // Table blanks inside question-box: get number from preceding marker (<1>, <2>) so we get 1 and 2, not box "1" twice
  doc.querySelectorAll('table .tiptap-input-field').forEach(span => {
    if (!span.closest('.question-box-content')) return;
    const parentP = span.closest('p');
    if (!parentP) return;
    const pHTML = parentP.innerHTML;
    const markers = [];
    const markerRegex = /(&lt;(\d+)&gt;|<(\d+)>)/g;
    let m;
    while ((m = markerRegex.exec(pHTML)) !== null) {
      markers.push({ number: m[2] || m[3], index: m.index });
    }
    const spanHTML = span.outerHTML;
    const spanIndex = pHTML.indexOf(spanHTML);
    if (spanIndex === -1) return;
    let matched = null;
    for (let i = markers.length - 1; i >= 0; i--) {
      if (markers[i].index < spanIndex) {
        matched = markers[i];
        break;
      }
    }
    if (matched && !seen.has(matched.number)) {
      seen.add(matched.number);
      questionNumbers.push(matched.number);
    }
  });

  // Extract question numbers from table blanks NOT in question-box (expand range e.g. "1-2" so bottom nav shows circles)
  doc.querySelectorAll('table .tiptap-input-field').forEach(span => {
    if (span.closest('.question-box-content')) return;
    const qNum = extractQuestionNumberFromNode(span);
    if (!qNum) return;
    const expanded = expandQuestionRange(qNum);
    expanded.forEach(n => {
      if (!seen.has(n)) {
        seen.add(n);
        questionNumbers.push(n);
      }
    });
  });

  // Extract question numbers from selection fields (dropdowns) - map each to its preceding marker
  doc.querySelectorAll('p').forEach(p => {
    if (p.closest('.question-box')) {
      // Handle question-box paragraphs separately
      const boxContent = p.closest('.question-box')?.querySelector('.question-box-content');
      if (boxContent) {
        boxContent.querySelectorAll('p').forEach(boxP => {
          const pHTML = boxP.innerHTML;
          const markers = [];
          const selections = [];
          
          const markerRegex = /(&lt;(\d+)&gt;|<(\d+)>)/g;
          let markerMatch;
          while ((markerMatch = markerRegex.exec(pHTML)) !== null) {
            markers.push({
              number: markerMatch[2] || markerMatch[3],
              index: markerMatch.index
            });
          }
          
          boxP.querySelectorAll('.selection-field').forEach(span => {
            if (!span.closest('table')) {
              const spanHTML = span.outerHTML;
              const spanIndex = pHTML.indexOf(spanHTML);
              if (spanIndex !== -1) {
                selections.push({ span, index: spanIndex });
              }
            }
          });
          
          // Get question-box range for fallback
          const questionBox = boxP.closest('.question-box');
          let boxQNum = questionBox ? (questionBox.getAttribute('data-question-number') || questionBox.getAttribute('questionnumber')) : '';
          let boxQNumRange = null;
          if (boxQNum && boxQNum.includes('-')) {
            boxQNumRange = expandQuestionRange(boxQNum);
          }
          
          selections.forEach((selection, selectionIndex) => {
            let matchedMarker = null;
            for (let i = markers.length - 1; i >= 0; i--) {
              if (markers[i].index < selection.index) {
                matchedMarker = markers[i];
                break;
              }
            }
            
            // If matched marker found, use it
            if (matchedMarker && !seen.has(matchedMarker.number)) {
              seen.add(matchedMarker.number);
              questionNumbers.push(matchedMarker.number);
            }
            // Fallback: Use question-box range if available, otherwise use single number
            else {
              if (boxQNumRange && boxQNumRange.length > 0) {
                // Use range expansion - assign numbers in order
                if (selectionIndex < boxQNumRange.length) {
                  const qNum = boxQNumRange[selectionIndex];
                  if (!seen.has(qNum)) {
                    seen.add(qNum);
                    questionNumbers.push(qNum);
                  }
                }
              } else if (boxQNum && /^\d+$/.test(boxQNum) && !seen.has(boxQNum)) {
                // Single number fallback
                seen.add(boxQNum);
                questionNumbers.push(boxQNum);
              }
            }
          });
        });
      }
      return;
    }
    
    // Regular paragraphs
    const pHTML = p.innerHTML;
    const markers = [];
    const selections = [];
    
    const markerRegex = /(&lt;(\d+)&gt;|<(\d+)>)/g;
    let markerMatch;
    while ((markerMatch = markerRegex.exec(pHTML)) !== null) {
      markers.push({
        number: markerMatch[2] || markerMatch[3],
        index: markerMatch.index
      });
    }
    
    p.querySelectorAll('.selection-field').forEach(span => {
      if (!span.closest('table')) {
        const spanHTML = span.outerHTML;
        const spanIndex = pHTML.indexOf(spanHTML);
        if (spanIndex !== -1) {
          selections.push({ span, index: spanIndex });
        }
      }
    });
    
    selections.forEach(selection => {
      let matchedMarker = null;
      for (let i = markers.length - 1; i >= 0; i--) {
        if (markers[i].index < selection.index) {
          matchedMarker = markers[i];
          break;
        }
      }
      if (matchedMarker && !seen.has(matchedMarker.number)) {
        seen.add(matchedMarker.number);
        questionNumbers.push(matchedMarker.number);
      } else if (!matchedMarker) {
        let box = selection.span.closest('.question-box');
        let boxNum = box ? (box.getAttribute('data-question-number') || box.getAttribute('questionnumber')) : '';
        if (boxNum && !seen.has(boxNum)) {
          seen.add(boxNum);
          questionNumbers.push(boxNum);
        }
      }
    });
  });

  // Table selection fields inside question-box: get number from preceding marker (<3>, <4>) so we get 3 and 4
  doc.querySelectorAll('table .selection-field').forEach(span => {
    if (!span.closest('.question-box-content')) return;
    const parentP = span.closest('p');
    if (!parentP) return;
    const pHTML = parentP.innerHTML;
    const markers = [];
    const markerRegex = /(&lt;(\d+)&gt;|<(\d+)>)/g;
    let m;
    while ((m = markerRegex.exec(pHTML)) !== null) {
      markers.push({ number: m[2] || m[3], index: m.index });
    }
    const spanHTML = span.outerHTML;
    const spanIndex = pHTML.indexOf(spanHTML);
    if (spanIndex === -1) return;
    let matched = null;
    for (let i = markers.length - 1; i >= 0; i--) {
      if (markers[i].index < spanIndex) {
        matched = markers[i];
        break;
      }
    }
    if (matched && !seen.has(matched.number)) {
      seen.add(matched.number);
      questionNumbers.push(matched.number);
    }
  });

  // Extract question numbers from table selection fields NOT in question-box (expand range so bottom nav shows circles)
  doc.querySelectorAll('table .selection-field').forEach(span => {
    if (span.closest('.question-box-content')) return;
    const qNum = extractQuestionNumberFromNode(span);
    if (!qNum) return;
    const expanded = expandQuestionRange(qNum);
    expanded.forEach(n => {
      if (!seen.has(n)) {
        seen.add(n);
        questionNumbers.push(n);
      }
    });
  });

  // Extract question numbers from drag/drop targets
  doc.querySelectorAll('.drag-drop-field').forEach(field => {
    let qNum = field.getAttribute('data-question-number') || '';
    if (!qNum || !/^\d+$/.test(qNum)) {
      qNum = extractQuestionNumberFromNode(field);
    }
    if (qNum && /^\d+$/.test(qNum) && !seen.has(qNum)) {
      seen.add(qNum);
      questionNumbers.push(qNum);
    }
  });

  const markerText = doc.body?.textContent || '';
  const dropMarkerRegex = /\[Drop here for Q\s*(\d+)\]/gi;
  let dropMarkerMatch;
  while ((dropMarkerMatch = dropMarkerRegex.exec(markerText)) !== null) {
    const qNum = dropMarkerMatch[1];
    if (qNum && !seen.has(qNum)) {
      seen.add(qNum);
      questionNumbers.push(qNum);
    }
  }

  // Extract question numbers from MCQ containers
  doc.querySelectorAll('.mcq-container').forEach(mcqContainer => {
    const mcqType = mcqContainer.getAttribute('data-mcq-type') || 'single';
    const questionRange = mcqContainer.getAttribute('data-question-range') || '';
    
    if (mcqType === 'single') {
      // Preferred: explicit number on the container
      let qNum = mcqContainer.getAttribute('data-question-number') || '';
      if (qNum && /^\d+$/.test(qNum) && !seen.has(qNum)) {
        seen.add(qNum);
        questionNumbers.push(qNum);
        return;
      }

      // Backward compatibility: extract from question text (pattern: "1. ..." or "1) ...")
      if (!qNum) {
        const questionText = mcqContainer.querySelector('.mcq-question-text')?.textContent || '';
        const qtMatch = questionText.match(/^(\d+)\s*[\.\)]\s*/);
        if (qtMatch && qtMatch[1]) {
          qNum = qtMatch[1];
        }
      }

      // Fallback: try to find a question number from preceding content
      if (!qNum) {
        let prevSibling = mcqContainer.previousElementSibling;
        while (prevSibling && !qNum) {
          if (prevSibling.tagName === 'P') {
            const numberMatch = prevSibling.innerHTML.match(/(&lt;(\d+)&gt;|<(\d+)>)/);
            if (numberMatch) {
              qNum = numberMatch[2] || numberMatch[3];
              break;
            }
          }
          prevSibling = prevSibling.previousElementSibling;
        }
      }

      if (qNum && /^\d+$/.test(qNum) && !seen.has(qNum)) {
        seen.add(qNum);
        questionNumbers.push(qNum);
      }
    } else if (mcqType === 'multiple' && questionRange) {
      // For multiple-answer MCQ, expand the range
      const expandedNums = expandQuestionRange(questionRange);
      expandedNums.forEach(qNum => {
        if (!seen.has(qNum)) {
          seen.add(qNum);
          questionNumbers.push(qNum);
        }
      });
    }
  });

  return questionNumbers.filter(n => n && /^\d+$/.test(n)).sort((a, b) => Number(a) - Number(b));
}

/**
 * Create mapping from question numbers to database question IDs
 */
export function createQuestionNumberToDbIdMap(sections) {
  const map = {};
  if (!sections || sections.length === 0) return map;
  
  sections.forEach((section) => {
    if (!section.questions || section.questions.length === 0) return;
    
    const passageText = section.passage_text || '';
    const parser = new DOMParser();
    const doc = parser.parseFromString(passageText, 'text/html');
    
    // Process question-box type questions
    const questionBoxQuestions = section.questions.filter(q => 
      q.question_type === 'question-box' && 
      q.question_html && 
      q.question_html.trim() !== ''
    );
    
    questionBoxQuestions.forEach((dbQuestion) => {
      const questionHtml = dbQuestion.question_html;
      const questionDoc = parser.parseFromString(questionHtml, 'text/html');
      const qNumbersInBox = new Set();
      
      // First priority: Get question numbers from question-box data attribute in passage_text
      const questionBoxes = doc.querySelectorAll('.question-box');
      questionBoxes.forEach(box => {
        const boxQNum = box.getAttribute('data-question-number') || box.getAttribute('questionnumber');
        if (boxQNum) {
          // Handle ranges like "1-5"
          if (boxQNum.includes('-')) {
            const parts = boxQNum.split('-');
            if (parts.length === 2) {
              const start = parseInt(parts[0].trim());
              const end = parseInt(parts[1].trim());
              if (!isNaN(start) && !isNaN(end)) {
                for (let i = start; i <= end; i++) {
                  qNumbersInBox.add(i.toString());
                }
              }
            }
          } else if (/^\d+$/.test(boxQNum.trim())) {
            qNumbersInBox.add(boxQNum.trim());
          }
        }
      });
      
      // Second priority: Extract question numbers from <1>, <2>, etc. in question_html
      // Use the same mapping logic as extractBlankNumberMappingAndCleanHtml - map each element to its nearest preceding marker
      questionDoc.querySelectorAll('p').forEach(p => {
        const pHTML = p.innerHTML;
        // Find all markers and interactive elements in order
        const markers = [];
        const blanks = [];
        const selections = [];
        
        // Find all angle bracket markers with their positions
        const markerRegex = /(&lt;(\d+)&gt;|<(\d+)>)/g;
        let markerMatch;
        while ((markerMatch = markerRegex.exec(pHTML)) !== null) {
          markers.push({
            number: markerMatch[2] || markerMatch[3],
            index: markerMatch.index
          });
        }
        
        // Find all blank spans
        p.querySelectorAll('.tiptap-input-field').forEach(span => {
          if (!span.closest('table')) {
            const spanHTML = span.outerHTML;
            const spanIndex = pHTML.indexOf(spanHTML);
            if (spanIndex !== -1) {
              blanks.push({ span, index: spanIndex });
            }
          }
        });
        
        // Find all selection spans
        p.querySelectorAll('.selection-field').forEach(span => {
          if (!span.closest('table')) {
            const spanHTML = span.outerHTML;
            const spanIndex = pHTML.indexOf(spanHTML);
            if (spanIndex !== -1) {
              selections.push({ span, index: spanIndex });
            }
          }
        });
        
        // Match each blank to its nearest preceding marker
        blanks.forEach(blank => {
          let matchedMarker = null;
          for (let i = markers.length - 1; i >= 0; i--) {
            if (markers[i].index < blank.index) {
              matchedMarker = markers[i];
              break;
            }
          }
          if (matchedMarker) {
            qNumbersInBox.add(matchedMarker.number);
          }
        });
        
        // Match each selection to its nearest preceding marker
        selections.forEach(selection => {
          let matchedMarker = null;
          for (let i = markers.length - 1; i >= 0; i--) {
            if (markers[i].index < selection.index) {
              matchedMarker = markers[i];
              break;
            }
          }
          if (matchedMarker) {
            qNumbersInBox.add(matchedMarker.number);
          }
        });
      });
      
      // Third priority: Extract from passage_text directly using the same mapping logic
      doc.querySelectorAll('p').forEach(p => {
        const pHTML = p.innerHTML;
        // Find all markers and interactive elements in order
        const markers = [];
        const blanks = [];
        const selections = [];
        
        // Find all angle bracket markers with their positions
        const markerRegex = /(&lt;(\d+)&gt;|<(\d+)>)/g;
        let markerMatch;
        while ((markerMatch = markerRegex.exec(pHTML)) !== null) {
          markers.push({
            number: markerMatch[2] || markerMatch[3],
            index: markerMatch.index
          });
        }
        
        // Find all blank spans
        p.querySelectorAll('.tiptap-input-field').forEach(span => {
          if (!span.closest('table')) {
            const spanHTML = span.outerHTML;
            const spanIndex = pHTML.indexOf(spanHTML);
            if (spanIndex !== -1) {
              blanks.push({ span, index: spanIndex });
            }
          }
        });
        
        // Find all selection spans
        p.querySelectorAll('.selection-field').forEach(span => {
          if (!span.closest('table')) {
            const spanHTML = span.outerHTML;
            const spanIndex = pHTML.indexOf(spanHTML);
            if (spanIndex !== -1) {
              selections.push({ span, index: spanIndex });
            }
          }
        });
        
        // Match each blank to its nearest preceding marker
        blanks.forEach(blank => {
          let matchedMarker = null;
          for (let i = markers.length - 1; i >= 0; i--) {
            if (markers[i].index < blank.index) {
              matchedMarker = markers[i];
              break;
            }
          }
          if (matchedMarker) {
            qNumbersInBox.add(matchedMarker.number);
          }
        });
        
        // Match each selection to its nearest preceding marker
        selections.forEach(selection => {
          let matchedMarker = null;
          for (let i = markers.length - 1; i >= 0; i--) {
            if (markers[i].index < selection.index) {
              matchedMarker = markers[i];
              break;
            }
          }
          if (matchedMarker) {
            qNumbersInBox.add(matchedMarker.number);
          }
        });
      });
      
      // Fallback: parse sample_answer if available
      if (qNumbersInBox.size === 0 && dbQuestion.sample_answer) {
        try {
          const sampleAnswer = typeof dbQuestion.sample_answer === 'string' 
            ? JSON.parse(dbQuestion.sample_answer) 
            : dbQuestion.sample_answer;
          if (typeof sampleAnswer === 'object' && sampleAnswer !== null) {
            if (Array.isArray(sampleAnswer)) {
              sampleAnswer.forEach(item => {
                if (item && item.questionNumber) {
                  qNumbersInBox.add(item.questionNumber.toString());
                }
              });
            } else {
              Object.keys(sampleAnswer).forEach(qNum => {
                if (/^\d+$/.test(qNum)) {
                  qNumbersInBox.add(qNum);
                }
              });
            }
          }
        } catch (e) {
          // ignore
        }
      }
      
      // Map each question number to the database question ID
      qNumbersInBox.forEach(qNum => {
        if (!map[qNum] || map[qNum] !== dbQuestion.id) {
          if (map[qNum] && map[qNum] !== dbQuestion.id) {
            console.warn(`Question number ${qNum} already mapped to ${map[qNum]}, now trying to map to ${dbQuestion.id}`);
          }
          map[qNum] = dbQuestion.id;
        }
      });
    });

    // Process drag/drop type questions
    const dragDropQuestions = section.questions.filter(q =>
      (q.question_type === 'drag-drop' || q.type === 'drag-drop' || String(q.question_html || '').includes('drag-drop-field'))
    );

    dragDropQuestions.forEach((dbQuestion) => {
      const qNumbersInDragDrop = new Set();
      const questionHtml = dbQuestion.question_html || '';
      const questionDoc = parser.parseFromString(questionHtml, 'text/html');

      questionDoc.querySelectorAll('.drag-drop-field').forEach(field => {
        const qNum = field.getAttribute('data-question-number') || '';
        if (qNum && /^\d+$/.test(qNum)) {
          qNumbersInDragDrop.add(qNum);
        }
      });

      if (dbQuestion.sample_answer) {
        try {
          const sampleAnswer = typeof dbQuestion.sample_answer === 'string'
            ? JSON.parse(dbQuestion.sample_answer)
            : dbQuestion.sample_answer;
          if (sampleAnswer && typeof sampleAnswer === 'object' && !Array.isArray(sampleAnswer)) {
            Object.keys(sampleAnswer).forEach(qNum => {
              if (/^\d+$/.test(qNum)) qNumbersInDragDrop.add(qNum);
            });
          }
        } catch (e) {
          // ignore
        }
      }

      qNumbersInDragDrop.forEach(qNum => {
        if (!map[qNum] || map[qNum] !== dbQuestion.id) {
          if (map[qNum] && map[qNum] !== dbQuestion.id) {
            console.warn(`Question number ${qNum} already mapped to ${map[qNum]}, now trying to map to ${dbQuestion.id}`);
          }
          map[qNum] = dbQuestion.id;
        }
      });
    });

    // Process MCQ type questions
    const mcqQuestions = section.questions.filter(q => 
      (q.question_type === 'mcq-single' || q.question_type === 'mcq-multiple') && 
      q.question_html && 
      q.question_html.trim() !== ''
    );
    
    mcqQuestions.forEach((dbQuestion) => {
      const questionHtml = dbQuestion.question_html;
      const questionDoc = parser.parseFromString(questionHtml, 'text/html');
      const mcqContainer = questionDoc.querySelector('.mcq-container');
      
      if (!mcqContainer) return;
      
      const mcqType = mcqContainer.getAttribute('data-mcq-type') || 'single';
      const questionRange = mcqContainer.getAttribute('data-question-range') || '';
      const qNumbersInMCQ = new Set();
      
      if (mcqType === 'single') {
        // Preferred: explicit number on the container
        let qNum = mcqContainer.getAttribute('data-question-number') || '';
        if (qNum && /^\d+$/.test(qNum)) {
          qNumbersInMCQ.add(qNum);
        }

        // Backward compatibility: extract from question text (pattern: "1. ..." or "1) ...")
        if (qNumbersInMCQ.size === 0) {
          const questionText = mcqContainer.querySelector('.mcq-question-text')?.textContent || '';
          const qtMatch = questionText.match(/^(\d+)\s*[\.\)]\s*/);
          if (qtMatch && qtMatch[1]) {
            qNum = qtMatch[1];
            qNumbersInMCQ.add(qNum);
          }
        }

        // Fallback: try to find question number from passage_text
        if (qNumbersInMCQ.size === 0) {
          const passageMcqs = doc.querySelectorAll('.mcq-container[data-mcq-type="single"]');
          passageMcqs.forEach((passageMcq) => {
            // Try to match by HTML structure or find preceding question marker
            let prevSibling = passageMcq.previousElementSibling;
            let qNum = null;
            while (prevSibling && !qNum) {
              if (prevSibling.tagName === 'P') {
                const numberMatch = prevSibling.innerHTML.match(/(&lt;(\d+)&gt;|<(\d+)>)/);
                if (numberMatch) {
                  qNum = numberMatch[2] || numberMatch[3];
                  break;
                }
              }
              prevSibling = prevSibling.previousElementSibling;
            }
            if (qNum) {
              qNumbersInMCQ.add(qNum);
            }
          });
        }
        
        // Final fallback: extract from sample_answer if it's an object with question number keys
        if (qNumbersInMCQ.size === 0 && dbQuestion.sample_answer) {
          try {
            const sampleAnswer = typeof dbQuestion.sample_answer === 'string' 
              ? JSON.parse(dbQuestion.sample_answer) 
              : dbQuestion.sample_answer;
            if (typeof sampleAnswer === 'object' && sampleAnswer !== null && !Array.isArray(sampleAnswer)) {
              // Check for single digit keys like {"1": "A"}
              Object.keys(sampleAnswer).forEach(key => {
                if (/^\d+$/.test(key)) {
                  qNumbersInMCQ.add(key);
                }
              });
            }
          } catch (e) {
            // ignore
          }
        }
      } else if (mcqType === 'multiple' && questionRange) {
        // For multiple-answer MCQ, expand the range
        const expandedNums = expandQuestionRange(questionRange);
        expandedNums.forEach(qNum => qNumbersInMCQ.add(qNum));
        
        // Also try to extract from sample_answer format: {"17-18": ["B", "D"]}
        if (qNumbersInMCQ.size === 0 && dbQuestion.sample_answer) {
          try {
            const sampleAnswer = typeof dbQuestion.sample_answer === 'string' 
              ? JSON.parse(dbQuestion.sample_answer) 
              : dbQuestion.sample_answer;
            if (typeof sampleAnswer === 'object' && sampleAnswer !== null) {
              // Check for range format keys
              Object.keys(sampleAnswer).forEach(key => {
                if (key.includes('-')) {
                  const expanded = expandQuestionRange(key);
                  expanded.forEach(qNum => qNumbersInMCQ.add(qNum));
                }
              });
            }
          } catch (e) {
            // ignore
          }
        }
      }
      
      // Map each question number to the database question ID (all questions in range map to same DB ID)
      qNumbersInMCQ.forEach(qNum => {
        if (!map[qNum] || map[qNum] !== dbQuestion.id) {
          if (map[qNum] && map[qNum] !== dbQuestion.id) {
            console.warn(`Question number ${qNum} already mapped to ${map[qNum]}, now trying to map to ${dbQuestion.id}`);
          }
          map[qNum] = dbQuestion.id;
        }
      });
    });
  });
  
  return map;
}

/**
 * Build answers array for API submission
 */
export function buildAnswersArray(answersObj, questionNumberToDbIdMap) {
  const answersByDbId = {};
  
  Object.entries(answersObj).forEach(([qnum, userAnswer]) => {
    if (!qnum || userAnswer === undefined || userAnswer === null || qnum === 'null' || !/^\d+$/.test(qnum)) {
      return;
    }
    
    const dbQuestionId = questionNumberToDbIdMap[qnum];
    
    if (!dbQuestionId) {
      console.warn(`No database question ID found for question number: ${qnum}`);
      return;
    }
    
    if (!answersByDbId[dbQuestionId]) {
      answersByDbId[dbQuestionId] = [];
    }
    
    answersByDbId[dbQuestionId].push({
      questionNumber: qnum,
      userAnswer: userAnswer || ''
    });
  });
  
  const result = [];
  Object.entries(answersByDbId).forEach(([dbQuestionId, answers]) => {
    answers.forEach(({ questionNumber, userAnswer }) => {
      result.push({
        questionId: parseInt(dbQuestionId),
        questionNumber: questionNumber,
        userAnswer: userAnswer
      });
    });
  });
  
  return result.filter(item => item.questionId);
}

/**
 * Check for skipped questions
 */
export function checkSkippedQuestions(sections, allAnswers, getSectionQuestionNumbersFn) {
  const allQuestionNumbers = [];
  sections.forEach((section, idx) => {
    const sectionQuestions = getSectionQuestionNumbersFn(idx);
    allQuestionNumbers.push(...sectionQuestions);
  });
  
  return allQuestionNumbers.filter(qNum => 
    !allAnswers[qNum] || allAnswers[qNum] === '' || allAnswers[qNum] === null || allAnswers[qNum] === undefined
  );
}
